import React, { useEffect } from 'react';
import { Form, Input, Switch, Button, Space } from 'antd';
import { EIQFormLabel } from 'common/components/label/form-label';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import './citations-configurations.scss';

interface RuleItemProps {
  index: number;
  name: number;
  restField: any;
  form: any;
  remove: (index: number) => void;
}

const CitationRuleConfiguration = ({ index, name, restField, remove, form }: RuleItemProps) => {
  const isWarningOnly = Form.useWatch(['rules', name, 'isWarningOnly'], { form });

  const isResidentFineAmountEnabled = Form.useWatch(['rules', name, 'isResidentFineAmountEnabled'], { form });
  const isResidentSuspendTransponderInDaysEnabled = Form.useWatch(['rules', name, 'isResidentSuspendTransponderInDaysEnabled'], { form });
  const isVisitorFineAmountEnabled = Form.useWatch(['rules', name, 'isVisitorFineAmountEnabled']);
  const isVisitorSuspendTransponderInDaysEnabled = Form.useWatch(['rules', name, 'isVisitorSuspendTransponderInDaysEnabled']);
  const isRevokeDrivingPrivilegesInDaysEnabled = Form.useWatch(['rules', name, 'isRevokeDrivingPrivilegesInDaysEnabled']);

  const getFieldValue = (fieldName: string) => {
    return form.getFieldValue(['rules', name, fieldName]);
  };

  useEffect(() => {
    if (isWarningOnly) {
      form.setFieldValue(['rules', name, 'isResidentFineAmountEnabled'], false);
      form.setFieldValue(['rules', name, 'isResidentSuspendTransponderInDaysEnabled'], false);
      form.setFieldValue(['rules', name, 'isVisitorFineAmountEnabled'], false);
      form.setFieldValue(['rules', name, 'isVisitorSuspendTransponderInDaysEnabled'], false);
      form.setFieldValue(['rules', name, 'isRevokeDrivingPrivilegesInDaysEnabled'], false);
    } else {
      form.setFieldValue(['rules', name, 'isResidentFineAmountEnabled'], !!getFieldValue('residentFineAmount'));
      form.setFieldValue(['rules', name, 'isResidentSuspendTransponderInDaysEnabled'], !!getFieldValue('residentSuspendTransponderInDays'));
      form.setFieldValue(['rules', name, 'isVisitorFineAmountEnabled'], !!getFieldValue('visitorFineAmount'));
      form.setFieldValue(['rules', name, 'isVisitorSuspendTransponderInDaysEnabled'], !!getFieldValue('visitorSuspendTransponderInDays'));
      form.setFieldValue(['rules', name, 'isRevokeDrivingPrivilegesInDaysEnabled'], !!getFieldValue('revokeDrivingPrivilegesInDays'));
    }
  }, [isWarningOnly]);

  return (
    <Space className="citation-rule-wrapper" direction="vertical">
      <div className="rule-index">Citation {index + 1}</div>

      <Form.Item {...restField} label={<EIQFormLabel title="Warning only" />} name={[name, 'isWarningOnly']} valuePropName="checked">
        <Switch />
      </Form.Item>

      <div className="resident-section">
        <div className="resident-section-title">Resident</div>
        <div className="resident-section-feilds">
          <div className="resident-section-feilds-item">
            <Form.Item name={[name, 'isResidentFineAmountEnabled']} valuePropName="checked">
              <Switch disabled={isWarningOnly} />
            </Form.Item>
            <Form.Item {...restField} label={<EIQFormLabel title="Resident fine ($)" />} name={[name, 'residentFineAmount']}>
              <Input type="number" min={0} step={0.1} disabled={isWarningOnly || !isResidentFineAmountEnabled} />
            </Form.Item>
          </div>

          <div className="resident-section-feilds-item">
            <Form.Item name={[name, 'isResidentSuspendTransponderInDaysEnabled']} valuePropName="checked">
              <Switch disabled={isWarningOnly} />
            </Form.Item>
            <Form.Item
              {...restField}
              label={<EIQFormLabel title="Resident Suspend Transponder (days)" />}
              name={[name, 'residentSuspendTransponderInDays']}>
              <Input type="number" min={0} step={1} disabled={isWarningOnly || !isResidentSuspendTransponderInDaysEnabled} />
            </Form.Item>
          </div>
        </div>
      </div>

      <div className="resident-section">
        <div className="resident-section-title">Visitor</div>
        <div className="resident-section-feilds">
          <div className="resident-section-feilds-item">
            <Form.Item name={[name, 'isVisitorFineAmountEnabled']} valuePropName="checked">
              <Switch disabled={isWarningOnly} />
            </Form.Item>
            <Form.Item {...restField} label={<EIQFormLabel title="Visitor fine ($)" />} name={[name, 'visitorFineAmount']}>
              <Input type="number" min={0} step={0.1} disabled={isWarningOnly || !isVisitorFineAmountEnabled} />
            </Form.Item>
          </div>

          <div className="resident-section-feilds-item">
            <Form.Item name={[name, 'isVisitorSuspendTransponderInDaysEnabled']} valuePropName="checked">
              <Switch disabled={isWarningOnly} />
            </Form.Item>
            <Form.Item
              {...restField}
              label={<EIQFormLabel title="Visitor Suspend Transponder (days)" />}
              name={[name, 'visitorSuspendTransponderInDays']}>
              <Input type="number" min={0} step={1} disabled={isWarningOnly || !isVisitorSuspendTransponderInDaysEnabled} />
            </Form.Item>
          </div>
          <div className="resident-section-feilds-item">
            <Form.Item name={[name, 'isRevokeDrivingPrivilegesInDaysEnabled']} valuePropName="checked">
              <Switch disabled={isWarningOnly} />
            </Form.Item>
            <Form.Item
              {...restField}
              label={<EIQFormLabel title="Revoke Driving Privileges (days)" />}
              name={[name, 'revokeDrivingPrivilegesInDays']}>
              <Input type="number" min={0} step={1} disabled={isWarningOnly || !isRevokeDrivingPrivilegesInDaysEnabled} />
            </Form.Item>
          </div>
        </div>
      </div>

      <Form.Item
        {...restField}
        label={
          <EIQFormLabel
            title="Legal Notes"
            description="These notes appear at the top of the email notification and on the PDF citation. Email addresses in the text will be automatically converted into clickable links."
          />
        }
        name={[name, 'legalNotes']}
        rules={[{ max: 1000, message: 'Maximum length is 1000 characters' }]}>
        <Input.TextArea rows={3} maxLength={1000} />
      </Form.Item>

      {index > 2 && (
        <Button type="text" icon={<Trash />} onClick={() => remove(name)}>
          Remove rule
        </Button>
      )}
    </Space>
  );
};

export default CitationRuleConfiguration;
